import { vdsEvent } from '../../base/events';
import { Logger } from '../../base/logger';
import { RequestQueue } from '../../base/queue';
import { DEV_MODE } from '../../global/env';
/**
 * A simple facade for dispatching media requests to the nearest media controller.
 *
 * @example
 * ```ts
 * import { MediaRemoteControl } from '@vidstack/elements';
 *
 * class MyElement extends HTMLElement {
 *   mediaRemoteControl = new MediaRemoteControl(this);
 *
 *   sendPlayRequest() {
 *     this.mediaRemoteControl.play();
 *   }
 * }
 * ```
 */
export class MediaRemoteControl {
    constructor(_host) {
        this._host = _host;
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger = new Logger(_host, { owner: this });
        }
        /* c8 ignore stop */
        this._connectedQueue = new RequestQueue(_host, 
        /* c8 ignore next */
        DEV_MODE && { name: 'remoteControlConnectedQueue', owner: this });
        if (_host instanceof Element)
            this.setRef(_host);
        _host.addController(this);
    }
    hostConnected() {
        this._connectedQueue.flush();
        this._connectedQueue.serveImmediately = true;
    }
    hostDisconnected() {
        this._connectedQueue.destroy();
    }
    /**
     * Set a reference to a DOM element that this controller will use as the target for dispatching
     * media requests from.
     */
    setRef(newRef) {
        if (this._ref !== newRef) {
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger.debug('ref change', newRef);
            }
            /* c8 ignore stop */
        }
        this._ref = newRef;
    }
    play(event) {
        this._dispatchRequest('vds-play-request', {
            originalEvent: event
        });
    }
    pause(event) {
        this._dispatchRequest('vds-pause-request', {
            originalEvent: event
        });
    }
    mute(event) {
        this._dispatchRequest('vds-mute-request', {
            originalEvent: event
        });
    }
    unmute(event) {
        this._dispatchRequest('vds-unmute-request', {
            originalEvent: event
        });
    }
    enterFullscreen(event) {
        this._dispatchRequest('vds-enter-fullscreen-request', {
            originalEvent: event
        });
    }
    exitFullscreen(event) {
        this._dispatchRequest('vds-exit-fullscreen-request', {
            originalEvent: event
        });
    }
    seeking(time, event) {
        this._dispatchRequest('vds-seeking-request', {
            detail: time,
            originalEvent: event
        });
    }
    seek(time, event) {
        this._dispatchRequest('vds-seek-request', {
            detail: time,
            originalEvent: event
        });
    }
    changeVolume(volume, event) {
        this._dispatchRequest('vds-volume-change-request', {
            detail: volume,
            originalEvent: event
        });
    }
    showControls(event) {
        this._dispatchRequest('vds-show-controls-request', {
            originalEvent: event
        });
    }
    hideControls(event) {
        this._dispatchRequest('vds-hide-controls-request', {
            originalEvent: event
        });
    }
    resumeIdleTracking(event) {
        this._dispatchRequest('vds-resume-idle-tracking-request', {
            originalEvent: event
        });
    }
    pauseIdleTracking(event) {
        this._dispatchRequest('vds-pause-idle-tracking-request', {
            originalEvent: event
        });
    }
    _dispatchRequest(type, eventInit) {
        this._connectedQueue.queue(type, () => {
            var _a;
            const request = vdsEvent(type, Object.assign(Object.assign({}, eventInit), { bubbles: true, composed: true }));
            /* c8 ignore start */
            if (DEV_MODE) {
                this._logger
                    .infoGroup(`📨 dispatching \`${type}\``)
                    .appendWithLabel('Event', request)
                    .appendWithLabel('Original event', eventInit.originalEvent)
                    .end();
            }
            /* c8 ignore stop */
            (_a = this._ref) === null || _a === void 0 ? void 0 : _a.dispatchEvent(request);
        });
    }
}
